import { useState } from 'react';
import {
  Home,
  Users,
  Network,
  CalendarClock,
  Inbox,
  Calendar,
  Megaphone,
  Settings,
  ShieldCheck,
  UserCircle2,
  KeyRound,
  Sparkles,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { openWelcomeWizard } from '@/components/WelcomeWizard';
import type { Role } from '@isoft-people/types';

type Module = {
  key: string;
  icon: any;
  title: string;
  tagline: string;
  roles: Role[];
  accent: string;
  bg: string;
  bullets: string[];
  to?: string;
  /** Render de la vista previa ilustrada del módulo */
  preview: () => JSX.Element;
};

/* ---------- Vistas previas ilustradas (mock UI, sin imágenes) ---------- */

function FramePreview({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-bg shadow-sm">
      {/* barra de ventana */}
      <div className="flex items-center gap-1.5 border-b border-border bg-white px-3 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-error/60" />
        <span className="h-2.5 w-2.5 rounded-full bg-warning/60" />
        <span className="h-2.5 w-2.5 rounded-full bg-accent/60" />
        <span className="ml-3 h-4 flex-1 rounded bg-border-light" />
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Bar({ w, c = 'bg-border' }: { w: string; c?: string }) {
  return <span className={`block h-2.5 rounded ${c}`} style={{ width: w }} />;
}

const dashboardPreview = () => (
  <FramePreview>
    <div className="grid grid-cols-4 gap-2">
      {['bg-primary-subtle', 'bg-red-50', 'bg-yellow-50', 'bg-accent-light'].map(
        (b, idx) => (
          <div key={idx} className="rounded-lg border border-border bg-white p-2">
            <div className={`h-5 w-5 rounded ${b}`} />
            <div className="mt-2 h-4 w-8 rounded bg-font-primary/80" />
            <div className="mt-1 h-1.5 w-full rounded bg-border" />
          </div>
        ),
      )}
    </div>
    <div className="mt-3 flex items-end justify-around gap-2 rounded-lg border border-border bg-white p-3">
      {[60, 90, 40, 75, 55].map((h, idx) => (
        <div
          key={idx}
          className={`w-4 rounded-t ${idx === 1 ? 'bg-primary' : 'bg-primary-light'}`}
          style={{ height: h }}
        />
      ))}
    </div>
  </FramePreview>
);

const employeesPreview = () => (
  <FramePreview>
    <div className="space-y-2">
      {[0, 1, 2, 3].map((r) => (
        <div
          key={r}
          className="flex items-center gap-3 rounded-lg border border-border bg-white p-2"
        >
          <div className="h-8 w-8 flex-shrink-0 rounded-full bg-gradient-to-br from-primary-light to-primary" />
          <div className="flex-1 space-y-1.5">
            <Bar w="55%" c="bg-font-primary/70" />
            <Bar w="35%" />
          </div>
          <span className="badge-success">Activo</span>
        </div>
      ))}
    </div>
  </FramePreview>
);

const orgPreview = () => (
  <FramePreview>
    <div className="flex flex-col items-center gap-3">
      <div className="rounded-lg bg-primary px-4 py-2">
        <Bar w="60px" c="bg-white/80" />
      </div>
      <div className="h-4 w-px bg-border" />
      <div className="flex gap-4">
        {[0, 1, 2].map((n) => (
          <div key={n} className="rounded-lg border border-border bg-white px-4 py-2">
            <Bar w="48px" c="bg-primary-light" />
          </div>
        ))}
      </div>
    </div>
  </FramePreview>
);

const leavePreview = () => (
  <FramePreview>
    <div className="space-y-2">
      <div className="flex items-center justify-between rounded-lg border border-accent/30 bg-accent-light/40 p-2">
        <div className="space-y-1.5">
          <Bar w="90px" c="bg-font-primary/70" />
          <Bar w="60px" />
        </div>
        <span className="badge-success">Aprobado</span>
      </div>
      <div className="flex items-center justify-between rounded-lg border border-warning/30 bg-yellow-50 p-2">
        <div className="space-y-1.5">
          <Bar w="80px" c="bg-font-primary/70" />
          <Bar w="50px" />
        </div>
        <span className="badge-warning">Pendiente</span>
      </div>
      <div className="flex items-center justify-between rounded-lg border border-border bg-white p-2">
        <div className="space-y-1.5">
          <Bar w="100px" c="bg-font-primary/70" />
          <Bar w="55px" />
        </div>
        <span className="badge-info">En curso</span>
      </div>
    </div>
  </FramePreview>
);

const inboxPreview = () => (
  <FramePreview>
    <div className="space-y-2">
      {[0, 1].map((r) => (
        <div
          key={r}
          className="flex items-center gap-3 rounded-lg border border-border bg-white p-2"
        >
          <div className="h-7 w-7 rounded-full bg-gradient-to-br from-primary-light to-primary" />
          <div className="flex-1 space-y-1.5">
            <Bar w="50%" c="bg-font-primary/70" />
            <Bar w="30%" />
          </div>
          <div className="flex gap-1.5">
            <span className="h-7 w-7 rounded-md bg-accent-light" />
            <span className="h-7 w-7 rounded-md bg-red-50" />
          </div>
        </div>
      ))}
    </div>
  </FramePreview>
);

const calendarPreview = () => (
  <FramePreview>
    <div className="grid grid-cols-7 gap-1.5">
      {Array.from({ length: 28 }).map((_, idx) => (
        <div
          key={idx}
          className={`flex aspect-square items-center justify-center rounded ${
            [4, 11, 12, 19].includes(idx)
              ? 'bg-primary-subtle'
              : 'border border-border-light bg-white'
          }`}
        >
          {[4, 11, 12, 19].includes(idx) && (
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          )}
        </div>
      ))}
    </div>
  </FramePreview>
);

const announcementsPreview = () => (
  <FramePreview>
    <div className="space-y-2">
      <div className="rounded-lg border-l-4 border-primary bg-white p-2.5 shadow-sm">
        <Bar w="70%" c="bg-font-primary/80" />
        <div className="mt-1.5 space-y-1">
          <Bar w="100%" />
          <Bar w="85%" />
        </div>
      </div>
      <div className="rounded-lg border border-border bg-white p-2.5">
        <Bar w="55%" c="bg-font-primary/70" />
        <div className="mt-1.5 space-y-1">
          <Bar w="95%" />
        </div>
      </div>
    </div>
  </FramePreview>
);

const configPreview = () => (
  <FramePreview>
    <div className="space-y-2">
      {['ADMIN', 'HR', 'MANAGER'].map((rol, idx) => (
        <div
          key={rol}
          className="flex items-center justify-between rounded-lg border border-border bg-white p-2"
        >
          <div className="space-y-1.5">
            <Bar w="110px" c="bg-font-primary/70" />
            <span className="badge-info">{rol}</span>
          </div>
          {idx === 0 && (
            <span className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] font-semibold text-font-secondary">
              <KeyRound size={11} /> Reiniciar
            </span>
          )}
        </div>
      ))}
    </div>
  </FramePreview>
);

const auditPreview = () => (
  <FramePreview>
    <div className="space-y-1.5">
      {[0, 1, 2, 3].map((r) => (
        <div
          key={r}
          className="flex items-center gap-2 rounded border border-border-light bg-white p-2 text-[10px]"
        >
          <span className="badge-info">UPDATE</span>
          <Bar w="40%" />
          <span className="ml-auto h-2 w-12 rounded bg-border" />
        </div>
      ))}
    </div>
  </FramePreview>
);

const profilePreview = () => (
  <FramePreview>
    <div className="flex flex-col items-center gap-2">
      <div className="h-14 w-14 rounded-full bg-gradient-to-br from-primary to-primary-light" />
      <Bar w="120px" c="bg-font-primary/80" />
      <Bar w="80px" />
      <div className="mt-2 w-full rounded-lg border border-border bg-white p-2">
        <div className="flex items-center gap-2">
          <KeyRound size={13} className="text-primary" />
          <Bar w="60%" />
        </div>
      </div>
    </div>
  </FramePreview>
);

/* ---------------------------- Módulos ---------------------------- */

const MODULES: Module[] = [
  {
    key: 'inicio',
    icon: Home,
    title: 'Inicio',
    tagline: 'El resumen de tu equipo de un vistazo',
    roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'],
    accent: 'text-primary',
    bg: 'bg-primary-subtle',
    to: '/',
    preview: dashboardPreview,
    bullets: [
      'Indicadores clave: headcount, ausencias del día y solicitudes pendientes.',
      'Gráfico de colaboradores por país para ver la distribución del equipo.',
      'Lista de aprobaciones pendientes con acceso directo a cada una.',
    ],
  },
  {
    key: 'colaboradores',
    icon: Users,
    title: 'Colaboradores',
    tagline: 'Directorio y expediente completo de cada persona',
    roles: ['ADMIN', 'HR', 'MANAGER'],
    accent: 'text-primary',
    bg: 'bg-primary-subtle',
    to: '/colaboradores',
    preview: employeesPreview,
    bullets: [
      'Busca y filtra el directorio por país, departamento o estado.',
      'Abre el perfil para ver datos, contacto, documentos, desempeño y cumplimiento.',
      'Da de alta nuevos colaboradores o edita la información existente (ADMIN/HR).',
    ],
  },
  {
    key: 'organigrama',
    icon: Network,
    title: 'Organigrama',
    tagline: 'La estructura jerárquica de la organización',
    roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'],
    accent: 'text-primary',
    bg: 'bg-primary-subtle',
    to: '/organigrama',
    preview: orgPreview,
    bullets: [
      'Visualiza quién reporta a quién en toda la empresa.',
      'Identifica líderes de equipo y líneas de mando rápidamente.',
      'Exporta la estructura a CSV cuando lo necesites.',
    ],
  },
  {
    key: 'permisos',
    icon: CalendarClock,
    title: 'Permisos y Vacaciones',
    tagline: 'Solicita y da seguimiento a tus días libres',
    roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'],
    accent: 'text-accent',
    bg: 'bg-accent-light',
    to: '/permisos',
    preview: leavePreview,
    bullets: [
      'Crea solicitudes de vacaciones o permisos en segundos.',
      'Consulta tu saldo de días disponibles y acumulados.',
      'Sigue el estado de cada solicitud: pendiente, aprobada o rechazada.',
    ],
  },
  {
    key: 'bandeja',
    icon: Inbox,
    title: 'Bandeja de aprobaciones',
    tagline: 'Aprueba o rechaza solicitudes de tu equipo',
    roles: ['ADMIN', 'HR', 'MANAGER'],
    accent: 'text-warning',
    bg: 'bg-yellow-50',
    to: '/bandeja',
    preview: inboxPreview,
    bullets: [
      'Todas las solicitudes que requieren tu decisión, en un solo lugar.',
      'Aprueba o rechaza con un clic; el colaborador recibe la notificación.',
      'Las pendientes aparecen resaltadas para que nada se quede esperando.',
    ],
  },
  {
    key: 'calendario',
    icon: Calendar,
    title: 'Calendario',
    tagline: 'Ausencias y feriados de tu equipo',
    roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'],
    accent: 'text-info',
    bg: 'bg-blue-50',
    to: '/calendario',
    preview: calendarPreview,
    bullets: [
      'Vista mensual con las ausencias programadas del equipo.',
      'Feriados oficiales por país marcados automáticamente.',
      'Planifica con anticipación evitando solapamientos.',
    ],
  },
  {
    key: 'anuncios',
    icon: Megaphone,
    title: 'Anuncios',
    tagline: 'Comunicados oficiales de la empresa',
    roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'],
    accent: 'text-info',
    bg: 'bg-blue-50',
    to: '/anuncios',
    preview: announcementsPreview,
    bullets: [
      'Lee los comunicados dirigidos a tu país o departamento.',
      'Un indicador te avisa cuando hay anuncios sin leer.',
      'ADMIN/HR pueden publicar anuncios con adjuntos y segmentación.',
    ],
  },
  {
    key: 'configuracion',
    icon: Settings,
    title: 'Configuración',
    tagline: 'Catálogos, roles, políticas y contraseñas',
    roles: ['ADMIN', 'HR'],
    accent: 'text-primary',
    bg: 'bg-primary-subtle',
    to: '/configuracion',
    preview: configPreview,
    bullets: [
      'Administra países, departamentos, puestos y políticas de vacaciones.',
      'Asigna roles del sistema a cada usuario.',
      'Reinicia la contraseña de un colaborador: el sistema genera una temporal que se muestra una sola vez y obliga al cambio en el primer ingreso.',
    ],
  },
  {
    key: 'auditoria',
    icon: ShieldCheck,
    title: 'Auditoría',
    tagline: 'Trazabilidad total de las acciones',
    roles: ['ADMIN'],
    accent: 'text-primary',
    bg: 'bg-primary-subtle',
    to: '/auditoria',
    preview: auditPreview,
    bullets: [
      'Registro de quién hizo qué y cuándo en toda la plataforma.',
      'Filtra por módulo y tipo de acción.',
      'Evidencia para cumplimiento y revisiones internas.',
    ],
  },
  {
    key: 'perfil',
    icon: UserCircle2,
    title: 'Mi perfil',
    tagline: 'Tus datos y tu contraseña',
    roles: ['ADMIN', 'HR', 'MANAGER', 'EMPLOYEE'],
    accent: 'text-accent',
    bg: 'bg-accent-light',
    to: '/mi-perfil',
    preview: profilePreview,
    bullets: [
      'Consulta tu información personal y laboral.',
      'Cambia tu contraseña cuando quieras desde el menú de tu avatar.',
      'Si un administrador te reinició la clave, se te pedirá actualizarla al entrar.',
    ],
  },
];

export function HelpPage() {
  const role = (useAuthStore((s) => s.user?.role) ?? 'EMPLOYEE') as Role;
  const navigate = useNavigate();
  const modules = MODULES.filter((m) => m.roles.includes(role));
  const [active, setActive] = useState(modules[0]?.key ?? 'inicio');
  const current = modules.find((m) => m.key === active) ?? modules[0];

  return (
    <div className="flex flex-col gap-6">
      {/* Hero */}
      <div className="brand-gradient relative overflow-hidden rounded-2xl px-4 py-6 sm:px-6 sm:py-7 lg:px-8 text-white anim-fade-up">
        <div className="relative z-10 flex items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/80">
              <Sparkles size={14} /> Centro de ayuda
            </div>
            <h1 className="mt-2 text-2xl font-bold">¿Qué puedes hacer en ISOFT People?</h1>
            <p className="mt-1.5 max-w-xl text-sm text-white/85">
              Explora cada módulo con una vista previa y los pasos clave. ¿Prefieres un
              recorrido guiado? Lanza el tutorial interactivo cuando quieras.
            </p>
          </div>
          <button
            onClick={openWelcomeWizard}
            className="hidden flex-shrink-0 items-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-primary shadow-lg transition hover:bg-white/90 md:inline-flex"
          >
            <Sparkles size={16} /> Ver tutorial
          </button>
        </div>
        <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10 anim-float" />
        <div className="pointer-events-none absolute -bottom-16 right-24 h-40 w-40 rounded-full bg-white/5" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
        {/* Índice de módulos */}
        <nav className="flex flex-col gap-1.5 lg:sticky lg:top-4 lg:self-start">
          {modules.map((m) => {
            const Icon = m.icon;
            const isActive = m.key === active;
            return (
              <button
                key={m.key}
                onClick={() => setActive(m.key)}
                className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm transition ${
                  isActive
                    ? 'border-primary/30 bg-primary-subtle font-semibold text-primary shadow-sm'
                    : 'border-transparent text-font-secondary hover:bg-white hover:text-font-primary'
                }`}
              >
                <span
                  className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md ${
                    isActive ? 'bg-white' : m.bg
                  }`}
                >
                  <Icon size={16} className={m.accent} />
                </span>
                <span className="truncate">{m.title}</span>
              </button>
            );
          })}
        </nav>

        {/* Detalle del módulo */}
        {current && (
          <div key={current.key} className="anim-fade-up">
            <div className="rounded-2xl border border-border bg-white p-6 shadow-sm lift">
              <div className="flex flex-wrap items-start gap-4">
                <span
                  className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${current.bg}`}
                >
                  <current.icon size={24} className={current.accent} />
                </span>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-font-primary">{current.title}</h2>
                  <p className="mt-0.5 text-sm text-font-secondary">{current.tagline}</p>
                </div>
                {current.to && (
                  <button
                    onClick={() => navigate(current.to!)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-primary-dark"
                  >
                    Abrir <ArrowRight size={15} />
                  </button>
                )}
              </div>

              <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Vista previa */}
                <div className="anim-scale-in">{current.preview()}</div>

                {/* Pasos clave */}
                <div>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-font-tertiary">
                    Lo que puedes hacer aquí
                  </p>
                  <ul className="flex flex-col gap-3">
                    {current.bullets.map((b, idx) => (
                      <li key={idx} className="flex items-start gap-2.5">
                        <CheckCircle2
                          size={17}
                          className="mt-0.5 flex-shrink-0 text-accent"
                        />
                        <span className="text-sm leading-relaxed text-font-primary">
                          {b}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <p className="mt-4 text-center text-xs text-font-tertiary">
              Las vistas previas son ilustrativas. Abre cada módulo para ver tus datos reales.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
